import PurpleRadialGradientBackground from "@/components/background/purple-radial-gradient";
import { PageContainer } from "@/components/container/page-container";
import { SmileIconWithShadow } from "@/components/icons/smile-icon-with-shadow";
import { RoundedModal } from "@/components/modal/rounded-modal";
import { FilledButton } from "@/components/ui/buttons";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { LocalStorageKeys } from "@/constants/local-storage-keys";
import { EditYourFoodParams, useEditYourFood } from "@/hooks/requests/your-food/use-edit-your-food";
import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { useIonRouter } from "@ionic/react";
import { memo, useEffect, useRef, useState } from "react";

const RecipeEditProcessingPage = memo(function RecipeEditProcessingPage() {
  const router = useIonRouter();
  const { editYourFoodAsync } = useEditYourFood();
  const hasRequestedRef = useRef(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { showSuccess } = useToast();

  useEffect(() => {
    if (hasRequestedRef.current) {
      return;
    }
    hasRequestedRef.current = true;

    const rawRequestPayload = localStorage.getItem(LocalStorageKeys.EDIT_YOUR_FOOD_REQUEST_KEY);
    localStorage.removeItem(LocalStorageKeys.EDIT_YOUR_FOOD_REQUEST_KEY);
    if (!rawRequestPayload) {
      setErrorMessage("Edit request data was not found. Please try editing the your food again.");
      return;
    }

    let requestPayload: EditYourFoodParams;
    try {
      requestPayload = JSON.parse(rawRequestPayload) as EditYourFoodParams;
    } catch {
      setErrorMessage("Failed to read edit request data. Please try again.");
      return;
    }

    void (async () => {
      try {
        await editYourFoodAsync(requestPayload);
        showSuccess("Your food edited successfully");
        router.push("/plans/meal-detail", "forward", "replace");
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : "Failed to update your food");
      }
    })();
  }, [editYourFoodAsync, router, showSuccess]);

  const handleDismissError = () => {
    router.push("/plans/meal-detail", "back", "replace");
  };

  return (
    <PageContainer>
      <PurpleRadialGradientBackground
        classNames={{
          container: cn("w-full !h-fit flex flex-col items-center min-h-screen px-8"),
          image: "bottom-0",
        }}
      >
        <div className="flex flex-1 flex-col items-center pt-4">
          <SmileIconWithShadow />
          <div className="fixed top-1/2 right-0 left-0 flex -translate-y-1/2 flex-col items-center gap-9">
            <div
              className={cn(
                "h-20.5 w-20.5 p-5",
                "flex items-center justify-center",
                "border-gray-theme rounded-4xl border bg-white",
              )}
            >
              <LoadingSpinner size="sm" />
            </div>
            <div className="flex flex-col items-center gap-4">
              <p className={cn(textVariants.bodySingleLine, "text-center")}>Editing your food...</p>
              <p className={cn(textVariants.footnote, "text-center")}>
                {!errorMessage && "This may take up to 15 seconds"}
              </p>
            </div>
          </div>
        </div>
      </PurpleRadialGradientBackground>

      <RoundedModal isOpen={!!errorMessage} onDidDismiss={handleDismissError} backdropDismiss>
        <div className="flex flex-col items-center space-y-6 p-6">
          <div className="space-y-4 text-center">
            <h2 className={cn(textVariants.title2)}>Unable to edit your food</h2>
            <p className={cn(textVariants.bodySingleLine, "text-text-gray")}>{errorMessage}</p>
          </div>

          <div className="w-full space-y-3">
            <FilledButton text="Go back" color="black" onClick={handleDismissError} />
          </div>
        </div>
      </RoundedModal>
    </PageContainer>
  );
});

export default RecipeEditProcessingPage;
